import React, { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiMessageCircle, FiX, FiArrowUpRight, FiHeadphones } from 'react-icons/fi';
import { AuthContext } from '../../contexts/auth';
import { supportChatApi as api } from '../../services/supportChatApi';
import ChatThread from './ChatThread';
import useChatPolling from './useChatPolling';
import regionConfig from '../../config/regionConfig';
import { moduleForPath } from './observationProtocol';
import './styles.css';

function Widget({ user }) {
  const { t } = useTranslation('supportChat');
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [conversationId, setConversationId] = useState(null);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [retry, setRetry] = useState(0);
  const panel = useRef(null);
  const trigger = useRef(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const support = user.role === 'Admin';

  useChatPolling(async signal => {
    try {
      const { data } = await api.get('/api/v1/support-chat/summary', { signal });
      if (!signal.aborted) setUnread(data.unread);
    } catch (_) { /* The thread shows connection errors when opened. */ }
  }, open ? 10000 : 30000);

  useEffect(() => {
    if (!open || support) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setError('');
    api.get('/api/v1/support-chat/mine', { signal: controller.signal }).then(({ data }) => {
      if (controller.signal.aborted) return;
      setConversationId(data.conversation?._id || null);
      setProfile(data.profile);
    }).catch(err => {
      if (!controller.signal.aborted) setError(t('widget.errorGeneric'));
    }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [open, support, retry, t]);

  useEffect(() => {
    if (!open) return undefined;
    const launcher = trigger.current;
    panel.current?.querySelector('button')?.focus();
    const keyboard = event => {
      if (event.key === 'Escape') setOpen(false);
      if (event.key === 'Tab') {
        const items = [...panel.current.querySelectorAll('button:not(:disabled), textarea:not(:disabled), a[href]')];
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener('keydown', keyboard);
    return () => { document.removeEventListener('keydown', keyboard); launcher?.focus(); };
  }, [open]);

  if (support && pathname === '/suporte') return null;

  return createPortal(
    <div className="eo-chat-widget">
      {open && (
        <section
          className="eo-chat-window"
          ref={panel}
          role="dialog"
          aria-label={t('widget.ariaWindow', { brand: regionConfig.brand.name })}
          aria-modal="false"
        >
          <header className="eo-chat-header">
            <div className="eo-chat-header-main">
              <div className="eo-chat-header-avatar">
                <FiHeadphones />
                <span className="eo-chat-online-dot" />
              </div>
              <div className="eo-chat-header-text">
                <div className="eo-chat-header-title-row">
                  <strong>{t('widget.title', { brand: regionConfig.brand.name })}</strong>
                  <span className="eo-chat-verified-badge">{t('widget.officialBadge')}</span>
                </div>
                <span className="eo-chat-header-sub">
                  <span className="eo-chat-status-pulse" />
                  {t('widget.statusLine')}
                </span>
              </div>
            </div>
            <button
              type="button"
              className="eo-chat-header-close"
              aria-label={t('widget.closeAria')}
              onClick={() => setOpen(false)}
            >
              <FiX />
            </button>
          </header>

          {loading && (
            <div className="eo-chat-loading-state" role="status">
              <span className="eo-chat-spinner" />
              <span>{t('widget.loading')}</span>
            </div>
          )}

          {error && (
            <div className="eo-chat-error" role="alert">
              <span>{error}</span>
              <button type="button" onClick={() => setRetry(value => value + 1)}>
                {t('widget.retry')}
              </button>
            </div>
          )}

          {!loading && !error && (
            <ChatThread
              key={conversationId || 'new'}
              conversationId={conversationId}
              profile={profile}
              onCreated={setConversationId}
              onChanged={() => setUnread(0)}
            />
          )}
        </section>
      )}

      <button
        ref={trigger}
        className={`eo-chat-launcher ${open ? 'is-open' : ''} ${unread > 0 ? 'has-unread' : ''}`}
        type="button"
        aria-expanded={open}
        aria-label={support ? t('widget.launcherOpenPanel') : t('widget.launcherOpenChat')}
        onClick={() => (support ? navigate('/suporte') : setOpen(value => !value))}
      >
        <span className="eo-chat-launcher-icon">
          {open ? <FiX /> : support ? <FiArrowUpRight /> : <FiMessageCircle />}
        </span>
        <span className="eo-chat-launcher-label">{support ? t('widget.launcherLabelPanel') : t('widget.launcherLabelChat')}</span>
        {unread > 0 && (
          <b className="eo-chat-launcher-badge" aria-label={t('widget.unreadAria', { count: unread })}>
            {unread > 99 ? '99+' : unread}
          </b>
        )}
      </button>
    </div>,
    document.body
  );
}

export default function SupportChat() {
  const { user } = useContext(AuthContext);
  const { pathname } = useLocation();
  if (!user || (!moduleForPath(pathname) && pathname !== '/suporte')) return null;
  return <Widget key={`${user._id || user.id}:${user.empresa_id || user.vinculo_empresa}`} user={user} />;
}
